import { VERSION_PHOTOS } from './version-history.config';
import { MAGIC_REPORT_BASE_STEPS } from '../magic-report-wizard.config';
import { MagicReportRevisionKind } from '../magic-report.type';

const KEY_LABELS: Record<string, string> = {
  'report.name': 'Report name',
  'report.coverLetter': 'Cover letter',
  'report.executiveSummary': 'Executive summary',
  'report.actionItems': 'Action items',
  'report.previousFundingsNote': 'Previous fundings note',
  'report.coverPage': 'Cover page',
  'report.siteConditions': 'Site conditions',
  'report.highlightedLineItemIds': 'Highlighted line items',
  'report.hiddenCoLienDrawIds': 'Hidden change order / lien waiver draws',
  'sections.prevFundings': 'Previous fundings',
  'sections.dynamicSections': 'Custom sections',
  'sections.scheduleGraphRows': 'Schedule graph',
  'sections.scheduleGraphAxis': 'Schedule graph',
  photos: 'Photos',
};

const humanize = (camel: string) => {
  const words = camel.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const labelForKey = (key: string) => KEY_LABELS[key] ?? humanize(key.replace(/^sections\./, ''));

/**
 * Distinct labels for a list of changed keys, in order (two keys can share a label). Photos are
 * left out while they aren't versioned (older versions may still list them).
 */
export const labelsForKeys = (keys: string[]) =>
  Array.from(new Set(keys.filter(key => VERSION_PHOTOS || key !== 'photos').map(labelForKey)));

const STEP_TITLES: Record<string, string> = Object.fromEntries(MAGIC_REPORT_BASE_STEPS.map(step => [step.id, step.title]));

export const labelForStep = (step?: string | null) => (step ? (STEP_TITLES[step] ?? step) : undefined);

const KIND_LABELS: Partial<Record<MagicReportRevisionKind, string>> = {
  Baseline: 'Starting point',
  Export: 'Exported',
  RunThisMonth: 'Before Run this Month',
  Restore: 'Restored',
  Revert: 'Changes undone',
  RestoreChanges: 'Changes restored',
};

export const labelForKind = (kind: MagicReportRevisionKind) => KIND_LABELS[kind];

/** The API sends UTC timestamps without a zone suffix; read them as UTC. */
export const parseUtc = (value?: string | null) => (value ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`) : undefined);

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const dayLabel = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    ...(date.getFullYear() !== today.getFullYear() && { year: 'numeric' }),
  });
};

export const timeLabel = (date: Date) => date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

export const dateTimeLabel = (date: Date) => `${dayLabel(date)}, ${timeLabel(date)}`;
